import React from 'react'
import {
  Artboard,
  Page,
  useArtboard,
  useCurrentArtboard,
  useNavigation,
  usePages,
} from '@sketch-hq/sketch-prototype-player'
import { RouteParams } from '@sketch/constants'
import { useVersioning } from 'modules/versioning'
import { useParams } from 'react-router-dom'

import { Dropdown } from '@sketch/components'
import { useResponsiveDropdown } from 'components/ResponsiveDropdown'
import NavigationItem from 'modules/shares/components/HeaderNavigation/NavigationItem'
import {
  ChevronsIcon,
  DropdownItem,
} from 'modules/shares/components/HeaderNavigation/NavigationItem.styles'
import {
  ArtboardSelectorButton,
  StartPointIcon,
} from './PrototypeArtboardsNavigationItem.styles'

const DROPDOWN_STYLE = {
  maxHeight: '80vh',
  overflow: 'auto',
} as const

export function PrototypeArtboardsNavigationItem() {
  const { artboardUUID } = useParams<RouteParams<'PROTOTYPE_VIEW'>>()
  const prototypeName = useArtboard(artboardUUID)?.name

  const { restartPrototypeFrom } = useNavigation()
  const pages = usePages()
  const artboards = pages.flatMap(page => page.artboards)
  const currentArtboard = useCurrentArtboard()
  const isCurrentArtboardAStartPoint = currentArtboard?.isFlowHome ?? false

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ArtboardsDropdown,
    dropdownProps: {
      pages,
      currentArtboardId: artboardUUID,
      restartPrototypeFrom,
    },
    dropdownStyle: DROPDOWN_STYLE,
  })

  const artboardCount = artboards.length || 0

  if (artboardCount === 1) {
    return (
      <NavigationItem
        icon="prototypeStartPoint"
        label={artboards[0].name || ''}
      />
    )
  }

  return (
    <>
      <ArtboardSelectorButton
        data-testid="prototype-artboard-selector"
        {...buttonProps}
      >
        <NavigationItem
          label={prototypeName}
          icon={
            (isCurrentArtboardAStartPoint && 'prototypeStartPoint') || undefined
          }
        />
        <ChevronsIcon />
      </ArtboardSelectorButton>
      {content}
    </>
  )
}

interface ArtboardsDropdownProps {
  pages: Page[]
  currentArtboardId: string | undefined
  restartPrototypeFrom: (artboardId: string) => void
}

function ArtboardsDropdown({
  pages,
  currentArtboardId,
  restartPrototypeFrom,
}: ArtboardsDropdownProps) {
  const { getPathname, share } = useVersioning()
  const getArtboardLink = (artboard: Artboard) =>
    getPathname({
      routeKey: 'PROTOTYPE_VIEW',
      routeParams: {
        shareID: share.identifier,
        artboardUUID: artboard.id,
      },
    })

  return (
    <>
      {pages.map(page => (
        <div key={page.id}>
          <Dropdown.Header>{page.name}</Dropdown.Header>
          {[...page.artboards].reverse().map(artboard => (
            <DropdownItem
              key={artboard.id}
              isActive={match => !!match || artboard.id === currentArtboardId}
              to={getArtboardLink(artboard)}
              onClick={() => restartPrototypeFrom(artboard.id)}
            >
              {artboard.isFlowHome && <StartPointIcon />}
              {artboard.name}
            </DropdownItem>
          ))}
        </div>
      ))}
    </>
  )
}
